import { useLazyQuery } from '@apollo/client/react';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { css } from '@styled-system/css';
import { Formik } from 'formik';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';

import { Alert, Box, Flex, Grid, Text } from '~/components/blocks';
import { Button } from '~/components/partials/PatientBlocks/Button';
import { Loader } from '~/components/partials/PatientBlocks/Loader';
import { GetCheckinOrganizationForEntryDocument } from '~/graphql/generated/graphql';
import { getNode } from '~/graphql/utility';
import { MonoColor } from '~/styles/theme';

import { Header } from '../../layouts/Checkin/Header';
import { FormPane } from './FormPane';
import { Fields } from './types';
import { useCheckinEntry } from './use-checkin-entry';
import { validationSchema } from './validation';

type Query = {
  checkinOrganizationId?: string;
};

const initialValues: Fields = {
  givenName: '',
  familyName: '',
  phoneticGivenName: '',
  phoneticFamilyName: '',
  phoneNumber: '',
  isAgree: false,
};

const Content = styled('div')(({ theme }) =>
  css({
    backgroundColor: theme.colors.background.default,
    borderRadius: theme.radii.modal,
    border: `1px solid ${MonoColor.patientGrey01}`,
    marginTop: theme.space.l,
    marginLeft: theme.space.m,
    marginRight: theme.space.m,
    paddingX: theme.space.l,
    paddingBottom: theme.space.l,
  }),
);

const ButtonBox = styled(Box)(({ theme }) =>
  css({
    backgroundColor: theme.colors.background.default,
    padding: theme.space.l,
    bottom: 0,
    right: 0,
    left: 0,
    width: '100%',
    position: 'fixed',
  }),
);

export const Entry = () => {
  const theme = useTheme();
  const router = useRouter();
  const [hasInitialized, setHasInitialized] = useState(false);
  const { checkinOrganizationId } = router.query as Query;
  const [getCheckinOrganization, { data, error: checkinOrganizationError }] = useLazyQuery(
    GetCheckinOrganizationForEntryDocument,
    {
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-first',
    },
  );
  const checkinOrganization = getNode(data, 'CheckinOrganization');

  const { isChecking, error, handleSubmit, loading } = useCheckinEntry(checkinOrganizationId);

  useEffect(() => {
    if (checkinOrganizationId) {
      getCheckinOrganization({
        variables: {
          id: checkinOrganizationId,
        },
      });
    }
  }, [checkinOrganizationId, getCheckinOrganization]);

  useEffect(() => {
    if (checkinOrganizationError) {
      setHasInitialized(true);
    }
  }, [checkinOrganizationError]);

  useEffect(() => {
    if (data) {
      setHasInitialized(true);
    }
  }, [data]);

  if (!hasInitialized) {
    return null;
  }

  if (!checkinOrganization?.id || !checkinOrganization.active) {
    router.replace('/checkin/404');
    return null;
  }

  return (
    <Grid gridTemplateRows="min-content min-content auto min-content">
      <Header title={checkinOrganization.name} />
      <Loader open={loading} />
      <Formik
        validateOnChange={false}
        validateOnBlur={false}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {(formik) => (
          <>
            <Box maxWidth="640px" margin="0 auto" paddingTop="56px">
              <Content>
                <Flex
                  alignItems="center"
                  flexDirection="column"
                  justifyContent="center"
                  maxWidth="400px"
                  margin={`${theme.space.l} auto 0`}
                >
                  <Text fontWeight="bold" size="xl">
                    スマホでチェックイン
                  </Text>
                  <Text block mt={theme.space.l} size="l">
                    チェックインをすると、お薬の受け渡し準備ができたらスマホにお知らせします。
                  </Text>
                </Flex>
                {error && (
                  <Alert status="error" mt={theme.space.l}>
                    {error}
                  </Alert>
                )}
                <FormPane formik={formik} isChecking={isChecking} />
              </Content>
            </Box>
            <ButtonBox>
              <Box maxWidth="460px" margin="0 auto">
                <Button
                  type="submit"
                  sharp="default"
                  wide="fill"
                  onClick={formik.submitForm}
                  disabled={isChecking || !formik.values.isAgree}
                >
                  <Text size="l" color="white">
                    チェックイン
                  </Text>
                </Button>
              </Box>
            </ButtonBox>
          </>
        )}
      </Formik>
    </Grid>
  );
};
